import React, { useRef, useState } from 'react';
import { Box, CircularProgress, Grid, Pagination, Stack, Tooltip, Typography } from '@mui/material';
import { useDocuments } from '../document-vault/hooks/useDocuments';

import { useTranslation } from 'react-i18next';
import BasicTabs from '../../common/basic-tabs/BasicTabs';
import DetailsTabs from './components/DetailsTab';
import DocumentsTabs from './components/DocumentsTabs';
import NotesTab from '../../common/notes-tab/NotesTab';
import FormUpload from '../../common/form-upload/FormUpload';
import UploadButton from '../../common/upload-button/UploadButton';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import styles from './CustomerProfile.module.scss';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import CreateCustomerContactModal from '../../common/create-customer-contact-modal/CreateCustomerContactModal';
import DashboardIcon from '../../../assets/svg/dashboard.svg?react';
import BallotIcon from '../../../assets/svg/ballot.svg?react';
import Button from '@mui/material/Button';
import { ROUTES } from '../../common/constants/routes';
import { useCustomerDetails } from './hooks/useCustomerDetails';
import { CreateCustomerContactItemModal } from '../../common/create-customer-contact-item-modal/CreateCustomerContactItemModal';
import ContactsTabs from './components/ContactsTabs';
import { USER_PERMISSIONS } from '../../common/constants/permissions';
import { useCheckPermission } from '../../common/hooks/useCheckPermission';
import InfoIcon from '@mui/icons-material/Info';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import Preview from './components/preview-block/Preview';

const CustomerProfile = () => {
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const company = useAppSelector(selectAllCompanies)[0];
  const formUploadRef = useRef<HTMLDivElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const { customerId } = useParams();
  const parsedCustomerId = Number(customerId);
  const sortByDate = (a: any, b: any) => {
    return Number(new Date(b.created)) - Number(new Date(a.created));
  };

  const [searchParams] = useSearchParams();
  const initialTab = Number(searchParams.get('initialTab')) || 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [workflow, setWorkflow] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const { customer } = useCustomerDetails(parsedCustomerId);

  const {
    documents,
    documentsCount,
    currentPage,
    setCurrentPage,
    getRequestedPage,
    setShowWorkflowBreadcrumbs,
    showWorkflowBreadcrumbs,
    cardView,
    setCardView,
  } = useDocuments('customers', workflow, parsedCustomerId);
  const combinedDocuments = [...documents].slice().sort((a, b) => sortByDate(a, b));

  // Items per page - adjust this value according to your API
  const itemsPerPage = 20;
  // Calculate total pages
  const totalPages = Math.ceil(documentsCount / itemsPerPage);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    getRequestedPage(page);
  };

  const checkPermission = useCheckPermission();

  const { t } = useTranslation();

  const handleUploadClose = () => {
    setIsUploadOpen(false);
  };

  // Actually handles both open & close
  const handleUploadOpen = () => {
    setIsUploadOpen((prev) => !prev);
  };

  /*useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      if (
        targetElement.closest('.MuiMenuItem-root') || // Ignore clicks on MenuItem
        targetElement.closest('.MuiSelect-root') // Ignore clicks on Select
      ) {
        return;
      }
      if (
        formUploadRef.current &&
        !formUploadRef.current.contains(event.target as Node) &&
        uploadButtonRef.current &&
        !uploadButtonRef.current.contains(event.target as Node)
      ) {
        setIsUploadOpen(false);
      }
    };

    if (isUploadOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUploadOpen]);*/

  const toggleShowAddPreferredContactMethodModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const toggleShowAddContactModal = () => {
    setIsContactModalOpen((prev) => !prev);
  };

  const handleSuccessfulSubmit = () => {
    setIsModalOpen((prev) => !prev);
    setActiveTab(1);
  };

  const handleSuccessfulSubmitContactModal = () => {
    setIsContactModalOpen(false);
    setActiveTab(2);
  };

  if (!company || !customer) {
    return (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 104px)',
        }}
      >
        <CircularProgress color='primary' size={50} />
      </Box>
    );
  }

  return (
    <Grid container spacing={3} sx={{ mt: '-54px' }}>
      <Grid size={{ xs: 12 }}>
        <Stack spacing={2}>
          <Box className={styles.breadcrumbs}>
            <Link className={styles.documentViewText} to={ROUTES.CUSTOMER_PROFILES}>
              {t('pages.customerProfiles')}
            </Link>
            <Typography variant='body2' className={styles.separator}>
              ›
            </Typography>
            <Typography variant='body2' className={styles.documentViewText}>
              {customer.customer_name}
            </Typography>
          </Box>
          <Typography variant='h1' component='h1' className={styles.page_title}>
            {customer.customer_name}
          </Typography>
          <Stack direction='row' alignItems={'center'} spacing={2} style={{ marginTop: '8px' }}>
            <Box ref={uploadButtonRef}>
              {checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_CREATE) &&
              checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_CUSTOMER_MANAGE) &&
              checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_ASSIGN_PROFILE) ? (
                <UploadButton
                  onClick={handleUploadOpen}
                  buttonText={t('buttons.uploadFiles')}
                  isArrowDown={false}
                />
              ) : (
                <Tooltip title={t('messages.contactAdministrator')} arrow placement='top'>
                  <UploadButton
                    onClick={handleUploadOpen}
                    buttonText={t('buttons.uploadFiles')}
                    isArrowDown={false}
                    disabled={true}
                  />
                </Tooltip>
              )}
            </Box>
            {isUploadOpen && (
              <Box className={styles.form_box} alignSelf={'flex-start'} ref={formUploadRef}>
                <FormUpload
                  onCancel={handleUploadClose}
                  categoryValue='customers'
                  customers={[customer]}
                  workflow={workflow}
                />
              </Box>
            )}
            <Tooltip
              title={
                checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_UPDATE)
                  ? t('labels.addPreferredContactMethod')
                  : t('messages.contactAdministrator')
              }
              arrow
              placement='top'
            >
              <Button
                disabled={!checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_UPDATE)}
                onClick={toggleShowAddPreferredContactMethodModal}
                className={styles.add_button}
              >
                {t('labels.addPreferredContactMethod')}
              </Button>
            </Tooltip>
            <Tooltip
              title={
                checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_CREATE_CONTACT)
                  ? t('buttons.addContact')
                  : t('messages.contactAdministrator')
              }
              arrow
              placement='top'
            >
              <Button
                onClick={toggleShowAddContactModal}
                disabled={!checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_CREATE_CONTACT)}
                className={styles.add_button}
              >
                {t('buttons.createContact')}
              </Button>
            </Tooltip>
            <PreviewTooltip title={<Preview />} arrow placement='top'>
              <InfoIcon color='primary' fontSize='small' />
            </PreviewTooltip>
            <Box display={'flex'} alignItems={'center'} gap={'8px'} marginLeft={'auto !important'}>
              <span style={{ fontSize: '14px' }}>{t('labels.documentsView')}: </span>
              {documents.length > 0 && workflow && (
                <Box className='pagination-block' sx={{ display: 'flex', justifyContent: 'end' }}>
                  <Typography className='pagination-pages-value' variant='caption'>
                    Page {currentPage} of {totalPages}
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    siblingCount={0}
                    boundaryCount={0}
                    // don't enable first/last buttons
                    /*renderItem={(item) => {
                                          if (item.type === 'previous' || item.type === 'next') {
                                            return <PaginationItem {...item} />;
                                          }
                                          // Render nothing for pages & ellipses so they don't exist in the DOM
                                          return null;
                                        }}*/
                    sx={{
                      // optional: tighten arrow spacing
                      '& .MuiPagination-ul': { gap: 0 },
                    }}
                  />
                </Box>
              )}
              <Box className={styles.card_list_view_container}>
                <button
                  className={`${styles.variant_btn} + ${cardView ? styles.variant_btn_active : ''}`}
                  onClick={() => setCardView(true)}
                >
                  <DashboardIcon />
                  <span>{t('buttons.card')}</span>
                </button>
                <button
                  onClick={() => {
                    setCardView(false);
                  }}
                  disabled={!!workflow && showWorkflowBreadcrumbs}
                  className={`${styles.variant_btn} + ${
                    !cardView ? styles.variant_btn_active : ''
                  }`}
                >
                  <BallotIcon />
                  <span>{t('buttons.list')}</span>
                </button>
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Grid>
      <Grid size={{ xs: 12 }} className={styles.grid_margin_bottom}>
        <BasicTabs
          tabs={[
            /*t('businessProfile.allTab'),*/
            t('businessProfile.documentsTabs'),
            t('businessProfile.detailsTab'),
            t('businessProfile.contactsTab'),
            t('businessProfile.notesTab'),
          ]}
          tabsChangeSideEffect={() => setWorkflow(null)}
          tabContents={[
            /*<AllTabs
                          customer={customer}
                          company={company}
                          documents={combinedDocuments}
                          key='all'
                        />,*/
            <DocumentsTabs
              documents={combinedDocuments}
              key='documents'
              cardView={cardView}
              workflow={workflow}
              setWorkflow={setWorkflow}
              showWorkflowBreadcrumbs={showWorkflowBreadcrumbs}
              setShowWorkflowBreadcrumbs={setShowWorkflowBreadcrumbs}
            />,
            <DetailsTabs customer={customer} company={company} key='details' />,
            <ContactsTabs customer={customer} companyId={company?.id} key='contacts' />,
            <NotesTab customer={customer} key='notes' />,
          ]}
          initialTab={initialTab}
          activeTab={activeTab}
        />
      </Grid>
      {isModalOpen && (
        <CreateCustomerContactModal
          customer={customer}
          companyId={company.id}
          open={isModalOpen}
          onClose={() => toggleShowAddPreferredContactMethodModal()}
          onSuccessfulSubmit={() => handleSuccessfulSubmit()}
        />
      )}
      {isContactModalOpen && (
        <CreateCustomerContactItemModal
          customerId={customer.id}
          companyId={company.id}
          open={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          onSuccessfulSubmit={() => handleSuccessfulSubmitContactModal()}
        />
      )}
    </Grid>
  );
};

export default CustomerProfile;
